import { useContext, useEffect, useMemo, useState } from 'react'

import { faArrowDownAZ } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ExpandMore } from '@material-ui/icons'
import { MultiSectionDialog } from '@scoir/modals'

import { useGuaranteedScid } from 'common/use-scid'
import ModalError from 'components/ModalError'
import { SortableList } from 'components/SortableList'

import { ContentItem, ContentType, ContentTypeEnum } from '../../ContentManagementProvider/Context'
import ContentManagementContext from '../../ContentManagementProvider/Context'
import { ReorderListItem } from './ReorderItem'

interface ContentReorderDetails {
  title: string
  description?: string
  type: string
  isSortable: boolean
}

type ContentData = {
  [K in ContentType]: ContentReorderDetails
}

type ContentItemMap = {
  [K in ContentType]: ContentItem[]
}

const contentMap: ContentData = {
  CollegeOverview: {
    title: 'Overview',
    description: 'The first post will be featured in Discovery. ',
    type: 'Posts',
    isSortable: true,
  },
  Academics: {
    title: 'Academics',
    type: 'Posts',
    isSortable: true,
  },
  Admissions: {
    title: 'Admissions',
    type: 'Posts',
    isSortable: true,
  },
  CostAndFinancialAid: {
    title: 'Cost & Aid',
    type: 'Posts',
    isSortable: true,
  },
  StudentLife: {
    title: 'Student Life',
    type: 'Posts',
    isSortable: true,
  },
  Sessions: {
    title: 'Events',
    description: 'Events will be sorted by date.',
    type: 'Events',
    isSortable: false,
  },
  Programs: {
    title: 'Programs',
    type: 'Programs',
    description: 'Programs will be sorted by last updated date.',
    isSortable: false,
  },
}

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow: 'none',
    padding: theme.spacing(1),
    // Override mui styling for disabled accordions
    backgroundColor: `${theme.palette.common.white} !important`,
  },
}))

export interface ContentReorderModalProps {
  isOpen: boolean
  onClose: () => void
}
const ContentReorderModal = ({ isOpen, onClose }: ContentReorderModalProps) => {
  const classes = useStyles()
  const scid = useGuaranteedScid()
  const { reorderContent, fetchReorderContent, fetchReorderContentLoading, saveNewOrder, saveNewOrderError } =
    useContext(ContentManagementContext)
  const [map, setMap] = useState<ContentItemMap | undefined>()

  useEffect(() => {
    if (isOpen) {
      fetchReorderContent()
    }
  }, [fetchReorderContent, isOpen])

  useEffect(() => {
    if (!fetchReorderContentLoading) {
      const contentMap: ContentItemMap = {
        CollegeOverview: [],
        Academics: [],
        Admissions: [],
        CostAndFinancialAid: [],
        StudentLife: [],
        Sessions: [],
        Programs: [],
      }
      reorderContent?.items
        ?.sort((a, b) => a.sortIndex - b.sortIndex)
        .forEach((s) => {
          if (contentMap[s.contentType]) {
            contentMap[s.contentType].push(s)
            return
          }
          contentMap[s.contentType] = [s]
        })
      setMap(contentMap)
    }
  }, [reorderContent, fetchReorderContentLoading])

  const handleAlphaSort = (type: ContentType) => {
    if (map) {
      const oldMap = map
      const alphaSortedItems = map[type].slice().sort((a, b) => a.title.localeCompare(b.title))
      setMap({
        ...map,
        [type]: alphaSortedItems,
      })
      const newOrder = alphaSortedItems.map((s) => s.id)
      saveNewOrder({
        scid,
        type,
        newOrder,
        onError: () => {
          setMap(oldMap)
        },
      })
    }
  }
  const contentAlphaSortedStatus = useMemo(() => {
    const status: { [key in ContentType]?: boolean } = {}
    if (map) {
      Object.keys(map).forEach((type) => {
        const items = map[type as ContentType]
        let sorted = true
        for (let i = 0; i < items.length - 1; i++) {
          if (items[i].title.localeCompare(items[i + 1].title) > 0) {
            sorted = false
            break
          }
        }
        status[type as ContentType] = sorted
      })
    }
    return status
  }, [map])
  const isContentAlphaSorted = (type: ContentType) => {
    return !!contentAlphaSortedStatus[type]
  }

  const [accordionStates, setAccordionStates] = useState<{ [key: number]: boolean }>({})
  const toggleAccordion = (idx: number) => {
    setAccordionStates((prevState) => ({
      ...prevState,
      [idx]: !prevState[idx],
    }))
  }
  const toggleAllAccordions = (open: boolean) => {
    if (map) {
      const updatedStates = Object.keys(map).reduce((acc: any, _, idx) => {
        acc[idx] = open
        return acc
      }, {})
      setAccordionStates(updatedStates)
    }
  }
  const handleOnClose = () => {
    onClose()
  }
  const allClosed = Object.values(accordionStates).every((state) => !state)

  if (!map) return null
  return (
    <MultiSectionDialog
      open={isOpen}
      onClose={onClose}
      scroll="paper"
      headerText="Reorder Content Display"
    >
      <MultiSectionDialog.Body>
        <Box
          display="flex"
          flexDirection="column"
          gridGap={16}
        >
          <Typography>
            Drag and drop to prioritize how your live content is displayed. Content will first be prioritized to match
            to student preferences, then displayed in the order set here.
          </Typography>
          <Box
            display="flex"
            flexDirection="column"
            gridGap={16}
          >
            {Object.keys(map).map((type, idx) => {
              const t = type as ContentType
              const items = map[t]
              const liveItems = items.filter((i) => !i.isHidden)

              const sectionDetails = contentMap[t]

              return (
                <Paper
                  elevation={0}
                  key={`${type}-${idx}`}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    width="100%"
                    gridGap={8}
                  >
                    <Accordion
                      disabled={liveItems.length === 0}
                      expanded={(accordionStates[idx] && liveItems.length > 0) ?? false}
                      onChange={() => toggleAccordion(idx)}
                      classes={{
                        root: classes.root,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore color={liveItems.length === 0 ? 'disabled' : 'inherit'} />}
                        aria-label={`Expand ${sectionDetails.title}`}
                        aria-controls={`${type + idx}-accordion-content`}
                        id={`${type + idx}-accordion-header`}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Box
                            display="flex"
                            flexDirection="column"
                          >
                            <Typography
                              variant="h6"
                              color="textPrimary"
                            >
                              {sectionDetails.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                            >
                              {sectionDetails.description
                                ? sectionDetails.description
                                : `${liveItems.length} ${sectionDetails.type}`}
                            </Typography>
                          </Box>
                          {accordionStates[idx] &&
                          (type === ContentTypeEnum.ACADEMICS || type === ContentTypeEnum.STUDENT_LIFE) ? (
                            <Button
                              onClick={(event) => {
                                event.stopPropagation()
                                handleAlphaSort(type as ContentType)
                              }}
                              disabled={isContentAlphaSorted(type as ContentType)}
                              variant="outlined"
                              color="primary"
                              startIcon={<FontAwesomeIcon icon={faArrowDownAZ} />}
                            >
                              Sort A-Z
                            </Button>
                          ) : null}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <SortableList
                          items={liveItems}
                          onChange={(items) => {
                            const oldMap = map
                            const updatedItems = items.map((item, index) => {
                              if (index === 0 && item.contentType === 'CollegeOverview') {
                                return { ...item, isFeatured: true }
                              } else {
                                return { ...item, isFeatured: false }
                              }
                            })
                            setMap({
                              ...map,
                              [t]: updatedItems,
                            })
                            const newOrder = items.map((s) => s.id)
                            saveNewOrder({
                              scid,
                              type: t,
                              newOrder,
                              onError: () => {
                                setMap(oldMap)
                              },
                            })
                          }}
                          renderItem={(item) => (
                            <SortableList.Item id={item.id}>
                              {sectionDetails.isSortable ? (
                                <SortableList.DragHandle>
                                  <ReorderListItem contentItem={item} />
                                </SortableList.DragHandle>
                              ) : (
                                <ReorderListItem
                                  contentItem={item}
                                  isSortable={sectionDetails.isSortable}
                                />
                              )}
                            </SortableList.Item>
                          )}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </Paper>
              )
            })}
          </Box>
        </Box>
      </MultiSectionDialog.Body>
      <MultiSectionDialog.Footer>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
        >
          {saveNewOrderError && <ModalError />}
          <Box
            display="flex"
            gridGap={8}
            alignItems="center"
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                toggleAllAccordions(allClosed)
              }}
            >
              {allClosed ? 'Expand All' : 'Close All'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOnClose}
            >
              Done
            </Button>
          </Box>
        </Box>
      </MultiSectionDialog.Footer>
    </MultiSectionDialog>
  )
}

export default ContentReorderModal
